import React from 'react';
import {useForm, useFieldArray, Controller} from "react-hook-form";
import {Require} from "../Require";
import {NavLink, useNavigate} from "react-router-dom";
import {office} from "../../../models/office";
import {scrollTo} from "../../../service/ToolsService";
import {officeMember} from "../../../models/officeMember";
import {member} from "../../../models/member";
import {ApiError, ApiService} from "../../../service/ApiService";
import {officeResultType} from "../../../types/Api/OfficeResultType";

type FormDataMemberType = {
  label: string,
  member: string,
  officeMemberId?: string,
  deletable: boolean,
}

type FormDataType = {
  label: string,
  enabled: boolean,
  officeMember: FormDataMemberType[],
}

type OfficeFormProps = {
  office?: office,
  officeMember?: officeMember[],
  member?: member[],
}

export const OfficeForm = ({office, officeMember, member}: OfficeFormProps) => {
  const navigate = useNavigate();

  const {
    control,
    register,
    handleSubmit,
    setError,
    watch,
    formState: {errors}
  } = useForm<FormDataType>({
    defaultValues: {
      label: office?.label ?? '',
      enabled: office?.enabled ?? false,
      officeMember: officeMember?.map((om: officeMember) => {
        return {label: om.label, member: om.member?.memberId ?? "", deletable: om.deletable, officeMemberId: om.officeMemberId}
      }),
    }
  });

  const {fields, append, remove} = useFieldArray({
    control,
    name: "officeMember",
  });

  const onSubmit = async (data: FormDataType) => {
    const d = {
      label: data.label,
      enabled: data.enabled ? 1 : 0,
    };

    const toDelete = officeMember?.filter(om =>
      !data.officeMember.some(dOm => dOm.officeMemberId === om.officeMemberId)
    );

    try {
      const result = office
        ? await ApiService.patch(`/office/${office.officeId}`, {param: () => d}) as officeResultType
        : await ApiService.post('/office', {param: () => d}) as officeResultType;

      for (const del of toDelete ?? []) {
        await ApiService.delete(`/office-member/${del.officeMemberId}`);
      }

      let order = 1;

      for (const dataOM of data.officeMember) {
        let dOM: { member_id: string|null, order: number, label?: string, office_id?: string } = {
          member_id: dataOM.member || null,
          order: order
        };

        order++;

        if (dataOM.deletable) {
          dOM.label = dataOM.label;
        }

        if (!dataOM.officeMemberId) {
          dOM.office_id = result.officeId;
        }

        dataOM.officeMemberId
          ? await ApiService.patch(`/office-member/${dataOM.officeMemberId}`, {param: () => dOM})
          : await ApiService.post('/office-member', {param: () => dOM});
      }


      navigate(office
        ? `/admin/office/office`
        : `/admin/office/office/edit/${result.officeId}`
      );
      scrollTo();
    } catch (e: any) {
      const error: ApiError = e as ApiError;

      error.errors?.forEach((e: {
        field: string,
        message: string
      }) => {
        setError(e.field as "label" | "enabled", {
          type: "manual",
          message: e.message
        })
      });
    }
  }

  return (
    <>
      <form className="p-3" method="post" onSubmit={handleSubmit(onSubmit)}>
        <div className="d-flex">
          <div className="col max-w600px pe-4">
            <div className="form-check form-switch mb-3">
              <input className="form-check-input" type="checkbox" id="form-enabled" {...register('enabled')} />
              <label className="form-check-label" htmlFor="form-enabled">
                {`${watch('enabled') ? 'Le bureau est actif' : 'Le bureau n\'est pas actif'}`}
              </label>
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="form-label">Année <Require/></label>
              <input id="form-label" type="text" className="form-control" placeholder="~" {...register('label', {
                required: 'L\'année est obligatoire',
                validate: {
                  minLength: (value) =>
                    (value ?? '').length <= 255 || 'L\'année ne doit pas dépasser 255 caractères',
                },
              })} />
              {errors.label && <p className="text-danger">{errors.label.message}</p>}
            </div>
          </div>
        </div>

        {officeMember && (
          <>
            <table className="table table-responsive border">
              <thead>
              <tr className="table-primary">
                <th>Poste</th>
                <th>Membre</th>
                <th className="w150px"></th>
              </tr>
              </thead>
              <tbody>
              {fields.map((field, index) => (
                <tr key={index}>
                  <td>
                    <input id="form-label" type="text" className="form-control" placeholder="~" {...register(`officeMember.${index}.label`, {
                      required: 'L\'année est obligatoire',
                      disabled: !field.deletable,
                      validate: {
                        minLength: (value) =>
                          (value ?? '').length <= 255 || 'L\'année ne doit pas dépasser 255 caractères',
                      },
                    })} />
                  </td>
                  <td>
                    <Controller
                      key={field.id}
                      control={control}
                      name={`officeMember.${index}.member`}
                      render={({field}) => (
                        <select {...field} className="form-select">
                          <option value="">~</option>
                          {
                            member && member.map((m: member, index) => (
                              <option key={index} value={m.memberId}>{m.firstname} {m.name}</option>
                            ))
                          }
                        </select>
                      )}
                    />
                  </td>
                  <td className="text-end align-middle">
                    {(officeMember[index]?.deletable ?? true) && (
                      <button className="btn btn-xs btn-danger" type="button" onClick={() => remove(index)}>
                        Supprimer
                      </button>
                    )}
                    <input
                      type="hidden"
                      {...register(`officeMember.${index}.officeMemberId`)}
                      value={undefined}
                    />
                  </td>
                </tr>
              ))

              }
              </tbody>
            </table>
            <div className="text-end">
              <button
                type="button"
                className="btn btn-xs btn-primary"
                onClick={() => append({label: "", member: "", deletable: true})}
              >
                Ajouter un poste
              </button>
            </div>
          </>
        )}


        <div className="mt-3">
          <button className="btn btn-sm btn-success me-1" type="submit">{office ? 'Enregistrer' : 'Suivant'}</button>
          <NavLink to={`/admin/office/office`} onClick={scrollTo} className="btn btn-sm btn-info me-1">
            Retour aux bureaux
          </NavLink>
        </div>
      </form>
    </>
  )
}
